import React, { useRef, useState } from "react";
import LockerMapMobile from "./LockerMapMobile";
import { useHistory } from "react-router-dom";
import CircularProgressLoader from "./CircularProgressLoader";
import ShowToast from "./ToastComponent";
import { BUTTON_PRIMARY, BUTTON_WARNING, TOAST_ERROR, TOAST_SUCCESS } from "../assets/constants/Constants";
import {useTranslation} from 'react-i18next';
import { getUnitLabelParams } from "../lib/Utils";
import { getSecureItemFromSpecificStorage } from "../lib/BrowserStorageAccessMiddleware";
import { LOCAL_STORAGE, LS_LOCKER_BANK_INFO } from "../assets/constants/BrowserStorageKeys";
import CustomDialog from "./CustomDialog";

import {
  fetchNearbyLockersForLocation,
  getCurrentBrowserLocation,
  getNearbyLockerErrorMessage,
  getOpenInChromeIntentUrl,
  isAndroidDevice,
  isInAppBrowser,
} from "../lib/NearbyLockerUtils";

const LockerMapPreview = () => {
  const [isLocating, setIsLocating] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isOpenInBrowserDialogVisible, setIsOpenInBrowserDialogVisible] = useState(false);
  const [isLocationBlockedDialogVisible, setIsLocationBlockedDialogVisible] = useState(false);
  const isFindingLockersRef = useRef(false);
  const { t } = useTranslation()
  const history = useHistory();
  const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
  const unitLabelParams = getUnitLabelParams(lockerBankInfo?.LockerBankType, t);

  // Default location (Pune)
  const defaultLocation = {
    lat: 18.5292,
    lng: 73.8746,
  };

  const handleFindLockers = async () => {
    if (isFindingLockersRef.current) {
      return;
    }

    isFindingLockersRef.current = true;
    setIsLocating(true);
    setShowLoader(true);
    setLoadingMessage(t('info_Message_Getting_Location'));

    try {
      const userLocation = await getCurrentBrowserLocation();
      const { lockersData: apiData } = await fetchNearbyLockersForLocation({
        userLocation,
        onLoadingMessage: setLoadingMessage,
      });

      setTimeout(() => {
        setShowLoader(false);

        history.push({
          pathname: "/map",
          state: {
            center: userLocation,
            useUserLocation: true,
            lockersData: apiData,
            timestamp: Date.now()
          },
        });
      }, 500);
    } catch (error) {
      console.log("Nearby locker flow error:", error);
      setShowLoader(false);

      // Instagram / Facebook in-app browsers often cannot give location: guide the user to open the page in a real browser.
      const isLocationError = typeof error?.code === 'string' && error.code.startsWith('LOCATION_');
      if (isLocationError && isInAppBrowser()) {
        setIsOpenInBrowserDialogVisible(true);
      } else if (error?.code === 'LOCATION_PERMISSION_DENIED') {
        // Real browsers cannot re-ask once location is blocked, so tell the user how to turn it on.
        setIsLocationBlockedDialogVisible(true);
      } else {
        ShowToast(getNearbyLockerErrorMessage(error), TOAST_ERROR);
      }
    } finally {
      isFindingLockersRef.current = false;
      setIsLocating(false);
    }
  };

  const handleOpenInBrowser = () => {
    const currentUrl = window.location.href;

    if (isAndroidDevice()) {
      window.location.href = getOpenInChromeIntentUrl(currentUrl);
      return;
    }

    // iOS: pages cannot open another browser, so copy the link for the user to paste in Safari or Chrome.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(currentUrl)
        .then(() => ShowToast(t('info_message_link_copied_paste_in_browser'), TOAST_SUCCESS))
        .catch(() => window.prompt(t('info_message_copy_this_link'), currentUrl));
    } else {
      window.prompt(t('info_message_copy_this_link'), currentUrl);
    }
  };

  return (
    <>
      <div className="locker-map-preview"  onClick={handleFindLockers}>
        <div className="preview-map-container">
          <div className="mini-map-wrapper">
            <LockerMapMobile
              isPreview={true}
              initialLocation={defaultLocation}
            />
          </div>
        </div>
        <div
          className={`find-lockers-btn-container ${
            isLocating ? "locating" : ""
          }`}
        >
          <h1 className="find-locker-heading">
            {isLocating
              ? t('info_Message_Getting_Location')
              : t('info_Search_Locker', unitLabelParams)}
          </h1>
        </div>
        {showLoader && (
        <CircularProgressLoader message={loadingMessage} />
      )}
      </div>

      {/* Kept outside the clickable preview so dialog clicks do not restart the locker search */}
      <CustomDialog
        dialogId="open-in-browser-dialog"
        dialogVisible={isOpenInBrowserDialogVisible}
        onClose={() => setIsOpenInBrowserDialogVisible(false)}
        dialogTitle={t('title_location_not_available')}
        dialogContentText={isAndroidDevice() ? t('info_message_open_in_chrome_for_location') : null}
        children={isAndroidDevice() ? null : () => (
          <div className="open-in-browser-steps">
            <p>{t('info_message_open_in_other_browser_for_location')}</p>
            <ol style={{ paddingLeft: 20, margin: 0, lineHeight: 1.6 }}>
              <li>{t('info_step_tap_copy_link')}</li>
              <li>{t('info_step_open_safari_or_chrome')}</li>
              <li>{t('info_step_paste_link_and_allow_location')}</li>
            </ol>
          </div>
        )}
        buttonOneTitle={t('button_close')}
        buttonTwoTitle={isAndroidDevice() ? t('button_open_in_chrome') : t('button_copy_link')}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY }
        }}
        handleCancel={() => setIsOpenInBrowserDialogVisible(false)}
        handleAccept={handleOpenInBrowser}
      />

      <CustomDialog
        dialogId="location-blocked-dialog"
        dialogVisible={isLocationBlockedDialogVisible}
        onClose={() => setIsLocationBlockedDialogVisible(false)}
        dialogTitle={t('title_location_permission_not_enabled')}
        dialogContentText={t('error_message_location_permission_blocked')}
        buttonOneTitle={t('button_ok')}
        buttonProps={{
          buttonOne: { type: BUTTON_PRIMARY }
        }}
        handleCancel={() => setIsLocationBlockedDialogVisible(false)}
      />
    </>

      
  );
};

export default LockerMapPreview;
